import type { TileKind, ZonePayload, ZoneTile } from '../../net/protocol';

/**
 * Which tile kinds can be stood on. Mirrors `TileKind.Walkable()` in Go.
 *
 * Both sides must agree exactly, the same way `worldCentre` and `tileCentre` do:
 * a client that thinks a house is walkable lets a player click into it and then
 * watches the server refuse to move them, which reads as the game ignoring input.
 */
export function isWalkable(kind: TileKind): boolean {
  switch (kind) {
    case 'VOID':
    case 'BUILDING':
    case 'RAMPART':
    // Terrain. An obstacle occupies its tile and a pool is liquid; both are
    // unwalkable, and only the first is solid — see isSolid.
    case 'OBSTACLE':
    case 'POOL':
      return false;
    default:
      return true;
  }
}

/**
 * Which tile kinds are a structure standing on the map, as opposed to simply
 * being off it. Mirrors `TileKind.Solid()` in Go.
 *
 * The renderer needs the distinction: void gets a wall drawn along the boundary
 * facing it, a structure gets a block drawn **on** it. Treat a house as void and
 * a town renders as a town-shaped hole.
 */
export function isSolid(kind: TileKind): boolean {
  return kind === 'BUILDING' || kind === 'RAMPART' || kind === 'OBSTACLE';
}

/**
 * Every tile occupied by a structure or by terrain, for the block builders.
 *
 * "Solid" answers *is a wall drawn against this* — no, it draws its own geometry —
 * and not *what geometry*. A pool is neither: unwalkable, not solid, and it paints
 * its own recessed surface, which is why `poolTiles` is a list of its own rather
 * than a case inside the structure builder.
 */
export function solidTiles(zone: ZonePayload): ZoneTile[] {
  return zone.tiles.filter((t) => isSolid(t.kind));
}

/**
 * The town's houses and curtain wall — solid tiles that are *built*.
 *
 * Split from `obstacleTiles` because the two are drawn differently: a building is
 * inset with a roof course, a boulder is not a building. Before terrain existed
 * `buildStructures` iterated every solid tile, so adding OBSTACLE to `isSolid`
 * without splitting this would have put a roof on every rock in the game.
 */
export function structureTiles(zone: ZonePayload): ZoneTile[] {
  return zone.tiles.filter((t) => t.kind === 'BUILDING' || t.kind === 'RAMPART');
}

/** Terrain blocks standing in a room. */
export function obstacleTiles(zone: ZonePayload): ZoneTile[] {
  return zone.tiles.filter((t) => t.kind === 'OBSTACLE');
}

/** Standing liquid, which draws a surface rather than a block. */
export function poolTiles(zone: ZonePayload): ZoneTile[] {
  return zone.tiles.filter((t) => t.kind === 'POOL');
}

/** The zone's half-extent in world units, for camera limits and fog. */
export function halfExtent(zone: ZonePayload): { x: number; z: number } {
  return {
    x: (zone.width * zone.tileSize) / 2,
    z: (zone.height * zone.tileSize) / 2,
  };
}

/**
 * The world position at the centre of a grid cell.
 *
 * Must match `worldCentre` in `internal/game/zone/generate.go` exactly — if the
 * two drift, the floor the client draws is not the floor the server lets you
 * walk on, and the mismatch is invisible until a player is standing in a wall.
 */
export function tileCentre(zone: ZonePayload, col: number, row: number): { x: number; z: number } {
  const half = halfExtent(zone);
  return {
    x: col * zone.tileSize + zone.tileSize / 2 - half.x,
    z: row * zone.tileSize + zone.tileSize / 2 - half.z,
  };
}

/**
 * How far an entity's centre is kept from the face of anything unwalkable. **Must equal
 * `BodyClearance` in `internal/game/zone/generate.go`**: prediction refuses the same steps the
 * server does, or the player leans into a wall the server will not let them into and is corrected
 * every frame. `layout.test.ts` and `generate_test.go` hold the same table of cases.
 */
export const BODY_CLEARANCE = 0.35;

/**
 * How far `p` sits inside the `BODY_CLEARANCE` margin of anything unwalkable next to its tile: 0
 * when clear, `Infinity` on an unwalkable tile. The mirror of `Layout.Intrusion` in Go, line for
 * line: an edge counts when the tile across it is unwalkable, an outer corner (a blocked diagonal
 * whose two neighbours are open) as a point.
 */
export function wallIntrusion(zone: ZonePayload, p: { x: number; z: number }): number {
  const half = halfExtent(zone);
  const col = Math.floor((p.x + half.x) / zone.tileSize);
  const row = Math.floor((p.z + half.z) / zone.tileSize);
  if (!isWalkable(tileAt(zone, col, row).kind)) return Infinity;
  const c = tileCentre(zone, col, row);
  const h = zone.tileSize / 2;
  const west = p.x - (c.x - h);
  const east = c.x + h - p.x;
  const north = p.z - (c.z - h);
  const south = c.z + h - p.z;
  const open = (dc: number, dr: number) => isWalkable(tileAt(zone, col + dc, row + dr).kind);
  let worst = 0;
  const deeper = (d: number) => {
    worst = Math.max(worst, BODY_CLEARANCE - d);
  };
  if (!open(-1, 0)) deeper(west);
  if (!open(1, 0)) deeper(east);
  if (!open(0, -1)) deeper(north);
  if (!open(0, 1)) deeper(south);
  const corner = (dc: number, dr: number, dx: number, dz: number) => {
    if (open(dc, 0) && open(0, dr) && !open(dc, dr)) deeper(Math.hypot(dx, dz));
  };
  corner(-1, -1, west, north);
  corner(1, -1, east, north);
  corner(-1, 1, west, south);
  corner(1, 1, east, south);
  return worst;
}

/**
 * Whether an entity at `from` may step to `to`: on walkable floor and no deeper inside a wall's
 * margin than it started. The rule `walkableAt` in `internal/game/movement.go` applies.
 */
export function canStep(zone: ZonePayload, from: { x: number; z: number }, to: { x: number; z: number }): boolean {
  const into = wallIntrusion(zone, to);
  if (into === Infinity) return false;
  return into <= Math.max(0, wallIntrusion(zone, from)) + 1e-9;
}

/** The tile at a grid coordinate, or a void tile when out of bounds. */
export function tileAt(zone: ZonePayload, col: number, row: number): ZoneTile {
  if (col < 0 || col >= zone.width || row < 0 || row >= zone.height) {
    return { kind: 'VOID', col, row };
  }
  return zone.tiles[row * zone.width + col] ?? { kind: 'VOID', col, row };
}

/**
 * Which sides of a walkable tile face into void, and therefore need a wall.
 *
 * Building walls per exposed edge rather than filling every void cell means a
 * 7 × 7 zone costs a few dozen boxes instead of hundreds, and the corridors read
 * as carved rather than as a field of blocks.
 */
export interface ExposedEdges {
  north: boolean;
  south: boolean;
  east: boolean;
  west: boolean;
}

export function exposedEdges(zone: ZonePayload, col: number, row: number): ExposedEdges {
  return {
    north: facesVoid(zone, col, row - 1),
    south: facesVoid(zone, col, row + 1),
    east: facesVoid(zone, col + 1, row),
    west: facesVoid(zone, col - 1, row),
  };
}

/**
 * Whether a neighbouring cell is off the map, and therefore needs a wall.
 *
 * **Void only, not merely unwalkable.** A structure is unwalkable too, but it
 * draws its own block filling its own tile — so putting a wall on that boundary as
 * well produces two pieces of geometry in the same place. In town that showed as a
 * glowing kerb along the foot of every house, because the wall's emissive trim was
 * drawn at the base of a building that already stood there.
 */
function facesVoid(zone: ZonePayload, col: number, row: number): boolean {
  return tileAt(zone, col, row).kind === 'VOID';
}

/** Every walkable tile, for the mesh builder to iterate. */
export function walkableTiles(zone: ZonePayload): ZoneTile[] {
  return zone.tiles.filter((t) => isWalkable(t.kind));
}

/**
 * A stable 0/1 checker value for a cell, so alternating floor shades do not
 * change when the zone is rebuilt.
 */
export function checker(col: number, row: number): 0 | 1 {
  return ((col + row) % 2 === 0 ? 0 : 1) as 0 | 1;
}
